import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, PRIMARY, SECONDARY } from '@glu/buttons-react';
import { useDispatch, useSelector } from 'react-redux';
import { createUseStyles } from '@glu/theming';
import LocaleContext from 'components/locale/LocaleContext';
import jsCookie from 'js-cookie';
// lodash
import get from 'lodash/get';

// api
import { getInfuseConfig, setInfuseConfig } from 'logic/store/infuse/actions';
import { auditInfuseHandShakeStatus } from 'api/infuse/service';

import { INFUSE_LOGIN, INFUSE } from 'constants.js';
import styles from './InfuseHandShakeScreen.styles';

// utils
import InfuseHandShake from './InfuseHandShake';

const useStyles = createUseStyles(styles);

export function InfuseHandShakeComponent({ logout, setHandShakeFlag }) {
  const dispatch = useDispatch();
  const locale = useContext(LocaleContext);
  const classes = useStyles();
  const infuseStore = useSelector((state) => get(state, 'infuse'));
  const infuse = Object.keys(infuseStore).length > 0 ? infuseStore : false;
  const config = infuse && infuse.config;
  const api = infuse && infuse.api;
  const state = infuse && infuse.state;

  const infuseStorage = JSON.parse(sessionStorage.getItem('infuse'));
  const [configLoaded, setConfigLoaded] = useState(false);

  function onInfuseClick(event) {
    const target = event.target;
    const checks = {
      isClose: target.id === `splash-${config.snippetId}-close-button`,
      isRemindMeLater: target.id === `splash-${config.snippetId}-remind-me-later`,
      isDownload: [target.id, target.parentNode.id].includes(
        `splash-${config.snippetId}-download-button`
      )
    };

    // check whether any of those has been clicked
    const closed =
      Object.keys(checks)
        .map((x) => checks[x])
        .filter(Boolean).length > 0;

    // if there's a match
    if (closed) {
      event.preventDefault();
      dispatch(setInfuseConfig({ ...config, store: { closed: +closed, completed: 1 } }));
    }
  }

  useEffect(() => {
    // in case it's not yet loaded
    if (!infuse) {
      dispatch(getInfuseConfig());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // add listener only if rapport is there
    if (config) {
      if (!infuseStorage || !get(infuseStorage, `${config.snippetId}.closed`)) {
        if (configLoaded) {
          document.addEventListener('click', onInfuseClick);
        }
      }
    }

    // Remove event listener on cleanup
    return () => {
      document.removeEventListener('click', onInfuseClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, configLoaded, infuseStorage]);

  useEffect(() => {
    // add listener only if rapport is there
    if (infuse && configLoaded) {
      if (!INFUSE_LOGIN.includes(config.handShakeEnabled)) {
        dispatch(setInfuseConfig({ store: { closed: 1, completed: 0 } }));
      }
      // eslint-disable-next-line react-hooks/rules-of-hooks
      InfuseHandShake.useAttachApi(infuse.config, dispatch);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infuse, configLoaded, infuseStorage]);
  // if configuration is already loaded, load api data
  if (infuse) {
    if (!configLoaded) {
      setConfigLoaded(true);
    }
  }

  const cookies = jsCookie.get() || {};
  const splashCookies = Object.keys(cookies)
    .map((cookieKey) => {
      if (cookieKey?.includes('splash_')) {
        if (cookies[cookieKey]?.includes('splash_first')) return cookieKey;
      }
      return null;
    })
    .filter(Boolean);
  // If they have a cookie from splash screen, then they've already seen this
  const hasAlreadySeenSplashRecently = splashCookies?.length > 0;

  const showSplash =
    api &&
    config &&
    !api.running &&
    !get(infuseStorage, `${config.snippetId}.closed`) &&
    !hasAlreadySeenSplashRecently &&
    INFUSE_LOGIN.includes(config.handShakeEnabled);

  // if configuration or api is not yet loaded
  if (!infuse || !config || !api) {
    return null;
  }
  if (api && api.isInstantiated === INFUSE.IS_UNDEFINED) {
    return (
      <div className={classes.container}>
        <div className="item-wrapper">
          <div>
            <p className="title">{locale['infuse.continuePhrase']}</p>
          </div>
          <div>
            <p className="title">{locale['infuse.cancelPhrase']}</p>
          </div>
          <div>
            <Button
              variant={PRIMARY}
              data-qa="continue-handshake"
              type="button"
              onClick={() => {
                setHandShakeFlag(true);
              }}
            >
              {locale['common.continue']}
            </Button>
            <Button
              variant={SECONDARY}
              data-qa="cancel-handshake"
              type="button"
              onClick={() => {
                logout();
              }}
            >
              {locale['common.cancel']}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  auditInfuseHandShakeStatus(api.running);

  if (!showSplash || get(state, `${config.snippetId}.closed`)) {
    dispatch(setInfuseConfig({ store: { closed: 1, completed: 1 } }));
    return null;
  }

  return (
    <div className={classes.root}>
      <div className={classes.content}>{InfuseHandShake.attachSplash(infuse.config)}</div>
    </div>
  );
}
InfuseHandShakeComponent.propTypes = {
  setHandShakeFlag: PropTypes.func,
  logout: PropTypes.func
};

InfuseHandShakeComponent.defaultProps = {
  setHandShakeFlag: () => {},
  logout: () => {}
};

export default InfuseHandShakeComponent;
